import { z } from 'zod/v4';
import { gridData } from '~/schemas/grid';

const demandAssociatedFilterValuesSchema = z
  .object({
    certificate_label: z.array(z.string()).nullable(),
    certificate_type: z.string().nullable(),
    counterparty_client_reference: z.string().nullable(),
    counterparty_id: z.uuid().nullable(),
    counterparty_name: z.string().nullable(),
    country: z.array(z.string()).nullable(),
    deal_client_reference: z.string().nullable(),
    deal_id: z.uuid().nullable(),
    deal_tranche_client_reference: z.string().nullable(),
    deal_tranche_id: z.uuid().nullable(),
    deal_tranche_volume_type: z.string().nullable(),
    demand_contract_type: z.string().nullable(),
    period_start_month: z.number().int(),
    period_start_year: z.number().int(),
    production_device_client_reference: z.string().nullable(),
    production_device_id: z.uuid().nullable(),
    production_device_name: z.string().nullable(),
    production_device_registry_identifier: z.string().nullable(),
    sub_national_region: z.array(z.string()).nullable(),
    subsidy_regime: z.array(z.string()).nullable(),
    supra_national_region: z.array(z.string()).nullable(),
    technology: z.array(z.string()).nullable(),
  })
  .catchall(z.string().nullable()); // Tag values are dynamic, so we need to allow any string value

const demandAssociatedFilterValuesTransformedSchema = demandAssociatedFilterValuesSchema.transform((data) => {
  const {
    deal_client_reference,
    deal_id,
    deal_tranche_client_reference,
    deal_tranche_id,
    deal_tranche_volume_type,
    production_device_client_reference,
    production_device_id,
    production_device_name,
    production_device_registry_identifier,
    ...rest
  } = data;
  return {
    ...rest,
    demand_sell_deal_client_reference: deal_client_reference,
    demand_sell_deal_id: deal_id,
    demand_sell_deal_tranche_client_reference: deal_tranche_client_reference,
    demand_sell_deal_tranche_id: deal_tranche_id,
    demand_sell_deal_tranche_volume_type: deal_tranche_volume_type,
    demand_production_device_client_reference: production_device_client_reference,
    demand_production_device_id: production_device_id,
    demand_production_device_name: production_device_name,
    demand_production_device_registry_identifier: production_device_registry_identifier,
    supply_buy_deal_client_reference: null,
    supply_buy_deal_id: null,
    supply_buy_deal_tranche_client_reference: null,
    supply_buy_deal_tranche_id: null,
    supply_buy_deal_tranche_volume_type: null,
    supply_contract_type: null,
    supply_production_device_client_reference: null,
    supply_production_device_id: null,
    supply_production_device_name: null,
    supply_production_device_registry_identifier: null,
    supply_type: null,
  };
});

export const demandAssociatedFilterValuesResponseSchema = gridData(
  z.object({
    data: z.array(demandAssociatedFilterValuesSchema),
  }),
);

export const demandAssociatedFilterValuesTransformedResponseSchema = gridData(
  z.object({
    data: z.array(demandAssociatedFilterValuesTransformedSchema),
  }),
);

const supplyAssociatedFilterValuesBaseSchema = z
  .object({
    certificate_label: z.array(z.string()).nullable(),
    certificate_type: z.string().nullable(),
    counterparty_client_reference: z.string().nullable(),
    counterparty_id: z.uuid().nullable(),
    counterparty_name: z.string().nullable(),
    deal_client_reference: z.string().nullable(),
    deal_id: z.uuid().nullable(),
    deal_tranche_client_reference: z.string().nullable(),
    deal_tranche_id: z.uuid().nullable(),
    deal_tranche_volume_type: z.string().nullable(),
    generation_country: z.array(z.string()).nullable(),
    period_start_month: z.number().int(),
    period_start_year: z.number().int(),
    production_device_client_reference: z.string().nullable(),
    production_device_id: z.uuid().nullable(),
    production_device_name: z.string().nullable(),
    production_device_registry_identifier: z.string().nullable(),
    registry: z.string().nullable(),
    registry_account_holder_id: z.uuid().nullable(),
    registry_account_holder_identifier: z.string().nullable(),
    registry_account_holder_name: z.string().nullable(),
    registry_account_id: z.uuid().nullable(),
    registry_account_identifier: z.string().nullable(),
    registry_account_name: z.string().nullable(),
    // Only sent with `include_dispatched`, and set on dispatched supply only.
    registry_transaction_id: z.uuid().nullable().optional(),
    sub_national_region: z.array(z.string()).nullable(),
    subsidy_regime: z.array(z.string()).nullable(),
    supply_contract_type: z.string().nullable(),
    supply_type: z.string().nullable(),
    supra_national_region: z.array(z.string()).nullable(),
    technology: z.array(z.string()).nullable(),
    transaction_registry_identifier: z.string().nullable().optional(),
  })
  .catchall(z.string().nullable()); // Tag values are dynamic, so we need to allow any string value

const supplyAssociatedFilterValuesSchema = supplyAssociatedFilterValuesBaseSchema.transform((data) => {
  const { generation_country, ...rest } = data;
  // We need to transform the generation_country to country as this is the field name used in the filters
  return {
    ...rest,
    country: generation_country,
  };
});

const supplyAssociatedFilterValuesTransformedSchema = supplyAssociatedFilterValuesSchema.transform((data) => {
  const {
    deal_client_reference,
    deal_id,
    production_device_id,
    production_device_client_reference,
    production_device_name,
    production_device_registry_identifier,
    deal_tranche_client_reference,
    deal_tranche_id,
    deal_tranche_volume_type,
    ...rest
  } = data;

  return {
    ...rest,
    demand_contract_type: null,
    demand_sell_deal_client_reference: null,
    demand_sell_deal_id: null,
    demand_sell_deal_tranche_client_reference: null,
    demand_sell_deal_tranche_id: null,
    demand_sell_deal_tranche_volume_type: null,
    demand_production_device_client_reference: null,
    demand_production_device_id: null,
    demand_production_device_name: null,
    demand_production_device_registry_identifier: null,
    supply_buy_deal_client_reference: deal_client_reference,
    supply_buy_deal_id: deal_id,
    supply_buy_deal_tranche_client_reference: deal_tranche_client_reference,
    supply_buy_deal_tranche_id: deal_tranche_id,
    supply_buy_deal_tranche_volume_type: deal_tranche_volume_type,
    supply_production_device_client_reference: production_device_client_reference,
    supply_production_device_id: production_device_id,
    supply_production_device_name: production_device_name,
    supply_production_device_registry_identifier: production_device_registry_identifier,
  };
});

export const supplyAssociatedFilterValuesResponseSchema = gridData(
  z.object({
    data: z.array(supplyAssociatedFilterValuesSchema),
  }),
);

export const supplyAssociatedFilterValuesTransformedResponseSchema = gridData(
  z.object({
    data: z.array(supplyAssociatedFilterValuesTransformedSchema),
  }),
);

const ecdashboardAssociatedFilterValuesSchema = z
  .object({
    counterparty_client_reference: z.string().nullable(),
    counterparty_id: z.uuid().nullable(),
    counterparty_name: z.string().nullable(),
    period_start_month: z.number().int(),
    period_start_year: z.number().int(),
  })
  .catchall(z.string().nullable()); // Tag values are dynamic, so we need to allow any string value

export const ecdashboardAssociatedFilterValuesResponseSchema = gridData(
  z.object({
    count: z.number(),
    data: z.array(ecdashboardAssociatedFilterValuesSchema),
  }),
);

export type DemandAssociatedFilterValues = z.infer<typeof demandAssociatedFilterValuesSchema>;
export type DemandAssociatedFilterKeys = keyof DemandAssociatedFilterValues;

export type SupplyAssociatedFilterValues = z.infer<typeof supplyAssociatedFilterValuesSchema>;

/**
 * The columns the demand payload declares. Constrained against the shape rather than through
 * `DemandAssociatedFilterValues`, whose tag catchall widens `keyof` to `string`.
 */
export type DemandAssociatedFilterColumn = keyof typeof demandAssociatedFilterValuesSchema.shape;

/**
 * The columns of a parsed supply row, for the same reason as {@link DemandAssociatedFilterColumn}. The schema
 * renames `generation_country` to `country`.
 */
export type SupplyAssociatedFilterColumn =
  | Exclude<keyof typeof supplyAssociatedFilterValuesBaseSchema.shape, 'generation_country'>
  | 'country';

export type ECDashboardAssociatedFilterValues = z.infer<typeof ecdashboardAssociatedFilterValuesSchema>;
export type ECDashboardAssociatedFilterKeys = keyof ECDashboardAssociatedFilterValues;
